/**
 * HealScene - Rest site: recover captured pieces or take gold.
 */

import Phaser from 'phaser';
import { GameStateManager } from '../game/GameStateManager';
import { SCENES, GAME_CONFIG } from '../constants/GameConstants';
import { PieceType } from '../types';
import { AudioManager } from '../game/AudioManager';
import { T, INK, PALETTE, makeButton, makeOptionCard, makePanel, paintBackground, fadeInScene } from '../constants/Theme';

export class HealScene extends Phaser.Scene {
  private gameStateManager!: GameStateManager;
  private hasUsedHeal: boolean = false;
  private selectedPiecesForRecovery: PieceType[] = [];

  constructor() {
    super({ key: SCENES.HEAL });
  }

  init(): void {
    this.gameStateManager = GameStateManager.getInstance();
    this.hasUsedHeal = false;
  }

  create(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const cx = WIDTH / 2;

    paintBackground(this, WIDTH, HEIGHT);
    fadeInScene(this);
    AudioManager.getInstance().init(this);

    const title = this.add.text(cx, 90, 'REST SITE', T.title({ color: INK.green }));
    title.setOrigin(0.5);
    title.setLetterSpacing(2);

    const subtitle = this.add.text(cx, 150, 'Choose one way to recover your forces', T.body());
    subtitle.setOrigin(0.5);

    const captured = this.gameStateManager.getGameState().capturedPieces.length;

    const options = [
      {
        name: 'Full Recovery', desc: 'Restore all captured pieces to your army',
        icon: '♥', accent: PALETTE.red, accentInk: INK.red,
        canUse: captured > 0, action: () => this.fullRecovery(),
      },
      {
        name: 'Selective Recovery', desc: 'Choose up to 2 captured pieces to restore',
        icon: '♡', accent: PALETTE.gold, accentInk: INK.gold,
        canUse: captured > 0, action: () => this.selectiveRecovery(),
      },
      {
        name: 'Bonus Gold', desc: 'Gain 100 gold for future purchases',
        icon: '◆', accent: PALETTE.blue, accentInk: INK.blue,
        canUse: true, action: () => this.gainGold(),
      },
    ];

    let y = 270;
    for (const o of options) {
      makeOptionCard(this, cx, y, o.name, o.desc, {
        icon: o.icon, accent: o.accent, accentInk: o.accentInk,
        enabled: o.canUse, note: o.canUse ? undefined : 'No captured pieces',
        onClick: () => { if (!this.hasUsedHeal) o.action(); },
      });
      y += 160;
    }

    makeButton(this, cx, HEIGHT - 100, 'SKIP', {
      fill: PALETTE.panelRaised, onClick: () => this.scene.start(SCENES.MAP),
    });
  }

  private fullRecovery(): void {
    const gameState = this.gameStateManager.getGameState();
    const count = gameState.capturedPieces.length;
    for (const pieceType of gameState.capturedPieces) {
      if (pieceType !== 'k') this.gameStateManager.addPiece(pieceType);
    }
    gameState.capturedPieces = [];
    this.gameStateManager.saveGame();
    this.hasUsedHeal = true;
    AudioManager.getInstance().play('upgrade');
    this.showFeedback(`Restored ${count} piece${count > 1 ? 's' : ''}!`, INK.green);
    this.time.delayedCall(1800, () => this.scene.start(SCENES.MAP));
  }

  private selectiveRecovery(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const cx = WIDTH / 2;
    this.children.removeAll();
    paintBackground(this, WIDTH, HEIGHT);
    this.selectedPiecesForRecovery = [];

    const title = this.add.text(cx, 90, 'CHOOSE PIECES', T.title({ color: INK.gold }));
    title.setOrigin(0.5);
    title.setLetterSpacing(2);
    const instr = this.add.text(cx, 150, 'Select up to 2 captured pieces to restore', T.body());
    instr.setOrigin(0.5);

    const counts = new Map<PieceType, number>();
    for (const p of this.gameStateManager.getGameState().capturedPieces) {
      counts.set(p, (counts.get(p) || 0) + 1);
    }

    let y = 240;
    for (const [type, count] of counts) {
      this.createCapturedPieceRow(cx, y, type, count);
      y += 100;
    }

    makeButton(this, cx, HEIGHT - 180, 'CONFIRM', {
      fill: PALETTE.green, onClick: () => {
        if (this.selectedPiecesForRecovery.length === 0) return;
        this.confirmSelectiveRecovery(this.selectedPiecesForRecovery);
      },
    });
    makeButton(this, cx, HEIGHT - 90, 'BACK', {
      width: 240, fontSize: 24, fill: PALETTE.panelRaised, onClick: () => this.scene.restart(),
    });
  }

  private createCapturedPieceRow(x: number, y: number, type: PieceType, count: number): void {
    const w = 520, h = 84;
    const panel = makePanel(this, x, y, w, h, { fill: PALETTE.panel, stroke: PALETTE.stroke, radius: 16 });

    const sprite = this.add.sprite(x - w / 2 + 50, y, `w${type}`);
    sprite.setDisplaySize(58, 58);

    const name = this.add.text(x - w / 2 + 100, y, `${this.getPieceName(type)} ×${count}`, T.heading({ fontSize: '28px' }));
    name.setOrigin(0, 0.5);

    const check = this.add.text(x + w / 2 - 40, y, '✓', { fontFamily: T.body().fontFamily, fontSize: '34px', color: INK.green });
    check.setOrigin(0.5);
    check.setVisible(false);

    const hit = this.add.rectangle(x, y, w, h, 0x000000, 0.001).setInteractive({ useHandCursor: true });
    hit.on('pointerdown', () => {
      const sel = this.selectedPiecesForRecovery;
      const idx = sel.indexOf(type);
      if (idx >= 0) {
        sel.splice(idx, 1);
        check.setVisible(false);
        panel.clear();
        panel.fillStyle(PALETTE.panel, 1); panel.fillRoundedRect(x - w / 2, y - h / 2, w, h, 16);
        panel.lineStyle(2, PALETTE.stroke, 1); panel.strokeRoundedRect(x - w / 2, y - h / 2, w, h, 16);
      } else if (sel.length < 2) {
        sel.push(type);
        check.setVisible(true);
        panel.clear();
        panel.fillStyle(PALETTE.panel, 1); panel.fillRoundedRect(x - w / 2, y - h / 2, w, h, 16);
        panel.lineStyle(3, PALETTE.green, 1); panel.strokeRoundedRect(x - w / 2, y - h / 2, w, h, 16);
      }
    });
  }

  private confirmSelectiveRecovery(selected: PieceType[]): void {
    const gameState = this.gameStateManager.getGameState();
    for (const type of selected) {
      const idx = gameState.capturedPieces.indexOf(type);
      if (idx >= 0 && type !== 'k') {
        gameState.capturedPieces.splice(idx, 1);
        this.gameStateManager.addPiece(type);
      }
    }
    this.gameStateManager.saveGame();
    this.hasUsedHeal = true;
    AudioManager.getInstance().play('upgrade');
    this.showFeedback(`Restored ${selected.length} piece${selected.length > 1 ? 's' : ''}!`, INK.gold);
    this.time.delayedCall(1800, () => this.scene.start(SCENES.MAP));
  }

  private gainGold(): void {
    this.gameStateManager.addGold(100);
    this.gameStateManager.saveGame();
    this.hasUsedHeal = true;
    AudioManager.getInstance().play('coin');
    this.showFeedback('Gained 100 gold!', INK.gold);
    this.time.delayedCall(1800, () => this.scene.start(SCENES.MAP));
  }

  private showFeedback(message: string, color: string): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const overlay = this.add.rectangle(0, 0, WIDTH, HEIGHT, 0x0a0f1c, 0.75).setOrigin(0);
    overlay.setDepth(300);
    const text = this.add.text(WIDTH / 2, HEIGHT / 2, message, T.title({ color, align: 'center' }));
    text.setOrigin(0.5);
    text.setDepth(301);
    text.setAlpha(0);
    this.tweens.add({ targets: text, alpha: 1, scale: { from: 0.9, to: 1 }, duration: 280, ease: 'Back.out' });
  }

  private getPieceName(type: string): string {
    const names: Record<string, string> = { p: 'Pawn', n: 'Knight', b: 'Bishop', r: 'Rook', q: 'Queen', k: 'King' };
    return names[type] || 'Unknown';
  }
}
